import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Alert } from "@/atoms";
import {
    AuthHeader,
    AuthInvalidState,
    AuthSuccessState,
    ResetPasswordForm,
} from "@/components/auth";
import { routes, strings } from "@/constants";
import { AlertVariantEnum } from "@/enums";
import { useResetPassword } from "@/hooks";
import { AuthLayout } from "@/layouts";
import { validatePasswordHandler } from "@/utils";

export const ResetPasswordPage = () => {
    const [token, setToken] = useState("");

    const [email, setEmail] = useState("");

    const [password, setPassword] = useState("");

    const [confirmPassword, setConfirmPassword] = useState("");

    const [validationError, setValidationError] = useState("");

    const [isSuccess, setIsSuccess] = useState(false);

    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const {
        error: serviceError,
        isLoading,
        onRun: runResetPasswordHandler,
    } = useResetPassword();

    const error = validationError || serviceError || "";

    const submitHandler = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setValidationError("");

        if (!token || !email) {
            setValidationError(strings.resetPassword.invalidLink);

            return;
        }

        const validation = validatePasswordHandler(password);

        if (!validation.isValid) {
            setValidationError(validation.error || strings.errors.checkInput);

            return;
        }

        if (password !== confirmPassword) {
            setValidationError(strings.resetPassword.passwordMismatch);

            return;
        }

        try {
            await runResetPasswordHandler({
                email,
                password,
                passwordConfirmation: confirmPassword,
                token,
            });

            setIsSuccess(true);
        } catch {
            // Error captured by hook
        }
    };

    const backToLoginHandler = () => navigate(routes.login);

    useEffect(
        () => {
            const tokenParam = searchParams.get(strings.queryParams.token);

            const emailParam = searchParams.get(strings.queryParams.email);

            if (tokenParam) setToken(tokenParam);

            if (emailParam) setEmail(emailParam);
        },
        [searchParams],
    );

    if (isSuccess) {
        return (
            <AuthLayout
                description={strings.resetPassword.sidebarDescription}
                title={strings.resetPassword.sidebarTitle}
            >
                <AuthSuccessState
                    actionLabel={strings.resetPassword.goToLogin}
                    message={strings.resetPassword.successMessage}
                    title={strings.resetPassword.title}
                    isFullWidth
                    onAction={backToLoginHandler}
                />
            </AuthLayout>
        );
    }

    if (!token || !email) {
        return (
            <AuthLayout
                description={strings.resetPassword.sidebarDescription}
                title={strings.resetPassword.sidebarTitle}
            >
                <AuthInvalidState
                    actionLabel={strings.resetPassword.backToLogin}
                    message={strings.resetPassword.invalidLink}
                    title={strings.resetPassword.title}
                    onAction={backToLoginHandler}
                />
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            description={strings.resetPassword.sidebarDescription}
            title={strings.resetPassword.sidebarTitle}
        >
            <div>
                <AuthHeader
                    description={strings.resetPassword.description}
                    title={strings.resetPassword.title}
                />
                {error && (
                    <Alert
                        className="mb-6"
                        variant={AlertVariantEnum.ERROR}
                        onClose={() => setValidationError("")}
                    >
                        {error}
                    </Alert>
                )}
                <ResetPasswordForm
                    confirmPassword={confirmPassword}
                    error={error}
                    isLoading={isLoading}
                    password={password}
                    onBackToLogin={backToLoginHandler}
                    onSubmit={submitHandler}
                    onChangeConfirmPassword={(value) => {
                        setConfirmPassword(value);

                        setValidationError("");
                    }}
                    onChangePassword={(value) => {
                        setPassword(value);

                        setValidationError("");
                    }}
                />
            </div>
        </AuthLayout>
    );
};
